import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'

import { dayjs } from '@hozana/dates'
import { useTimeout } from '@hozana/hooks/useTimeout'
import { useScreen } from '@hozana/screen/useScreen'
import { cookie } from '@hozana/storage/cookies'
import { GTM } from '@hozana/tracking/gtm'

import { Button } from 'elements/button/Button'
import { FixedDiv } from 'elements/layout/FixedDiv'
import { SelfDestroyingDiv } from 'elements/motion/SelfDestroyingDiv'

import { ICON } from 'config/icons'
import { QUERY_ACTION } from 'general/constants'
import { COOKIES_CONFIG } from 'general/managers/cookies/constants'
import { TCookieName } from 'general/managers/cookies/types'
import { POPINS } from 'general/managers/popins/constants'
import { getNewPrayerText } from 'general/popins/PrayerPromptPopin'

import { NOTIFICATION_TYPE } from 'modules/notification/constants'
import { useNotifications } from 'modules/notification/hooks/useNotifications'
import { useUser } from 'modules/user/hooks/useUser'

import { PromptType } from './constants'
import { usePromptsManagement } from './usePromptsManagement'

export type TPrayerPromptManagerProps = {
  cookieName: TCookieName
  notificationType:
    | NOTIFICATION_TYPE.NEW_PRAYER_ON_ARRIVAL
    | NOTIFICATION_TYPE.NEW_PRAYER_ON_BIRTHDAY
    | NOTIFICATION_TYPE.NEW_PRAYER_ON_INTENTION
}

/**
 * ### PrayerPromptManager ###
 *
 * Manage the prayer prompt.
 */
export const PrayerPromptManager: TFC<TPrayerPromptManagerProps> = ({ cookieName, notificationType }) => {
  const { t } = useTranslation()
  const me = useUser()

  const [notifications = []] = useNotifications({ type: notificationType })
  const [isAnimationOutTriggered, setIsAnimationOutTriggered] = useState(false)
  const [show, setShow] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const theme = useTheme()
  const screen = useScreen()

  const lastPromptTime = Number(cookie.load(cookieName))
  const lastPrayerNotifications = notifications
    .filter(
      (notification) =>
        (lastPromptTime &&
          dayjs(notification.date).valueOf() > lastPromptTime &&
          (notificationType !== NOTIFICATION_TYPE.NEW_PRAYER_ON_INTENTION || notification.objectId === me.intention)) ||
        !lastPromptTime,
    )
    // Sort in descending order
    .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
  const lastPrayerNotification = lastPrayerNotifications[0] // Get the latest date
  const lastNotificationTime = lastPrayerNotification && dayjs(lastPrayerNotification.date).valueOf()
  const prayersNumber = lastPrayerNotifications.length
  const subHeaderHeight = theme.sizes.subHeader

  useEffect(() => {
    if (prayersNumber > 0) {
      if (!lastPromptTime && lastPrayerNotification?.payload.user?.id) {
        setShow(true)
      } else if (lastPromptTime !== undefined && lastNotificationTime > lastPromptTime) {
        setShowBubble(true)
      }
    }
  }, [lastPromptTime, setShowBubble, prayersNumber, lastNotificationTime, lastPrayerNotification?.payload.user?.id])

  useTimeout(() => setIsAnimationOutTriggered(true), 4000, !showBubble || prayersNumber === 0)

  const updateCookie = useCallback(
    () => cookie.save(cookieName, lastNotificationTime, COOKIES_CONFIG.ONE_YEAR),
    [cookieName, lastNotificationTime],
  )

  const onPop = useCallback(() => {
    updateCookie()
    setShow(false)
    GTM.trackEvent(GTM.EVENTS.THANKS_PRAYER_DISPLAYED)
  }, [updateCookie])

  usePromptsManagement({
    type: PromptType.PRAYER,
    show,
    actionKey: QUERY_ACTION.THANK_PRAYER,
    onPop,
    popinName: POPINS.PrayerPromptPopin,
    popinOptions: lastPrayerNotification,
    onClosePopin: () => GTM.trackEvent(GTM.EVENTS.THANKS_PRAYER_DISMISSED),
  })

  if (!showBubble) {
    return null
  }

  const text = t(getNewPrayerText(notificationType)[prayersNumber > 1 ? 'multiple' : 'single'], {
    amount: prayersNumber,
    userName: lastPrayerNotification?.payload.user?.name || '_', // '_' is translated to 'someone'
  })

  const pathname =
    lastPrayerNotification &&
    {
      [NOTIFICATION_TYPE.NEW_PRAYER_ON_INTENTION]: `/intention/intention/${lastPrayerNotification.objectId}/prayers`,
      [NOTIFICATION_TYPE.NEW_PRAYER_ON_BIRTHDAY]: `/intention/birthday/${lastPrayerNotification.objectId}/prayers`,
      [NOTIFICATION_TYPE.NEW_PRAYER_ON_ARRIVAL]: `/intention/arrival/${lastPrayerNotification.objectId}/prayers`,
    }[notificationType]

  return (
    <FixedDiv
      left="0"
      m="1em 1em"
      bottom={screen.xs ? subHeaderHeight : '0'}
      zIndex="flashMessage"
      maxW={{ sm: '450px' }}
      bg="transparent"
      noPrint
    >
      <SelfDestroyingDiv
        origin="left"
        direction="left"
        w="100%"
        triggered={isAnimationOutTriggered}
        animationTime={500}
        onFinish={() => {
          // reset state for a next bubble to appear
          setShowBubble(false)
          setIsAnimationOutTriggered(false)
          // updating cookie here allows bubble to not appear again when changing page
          updateCookie()
        }}
      >
        <Button
          aria-label={text}
          icon={ICON.FIRE}
          p="5px 10px"
          colors="white"
          color="blue"
          hoverBg="blue"
          w="fit-content"
          size="tiny"
          as="a"
          shadow
          onClick={() => {
            updateCookie()
            // Remove bubble otherwise it stays on the screen
            setShowBubble(false)
            setIsAnimationOutTriggered(false)
          }}
          href={pathname}
        >
          {text}
        </Button>
      </SelfDestroyingDiv>
    </FixedDiv>
  )
}
